import React, { useState, useEffect, useRef } from 'react';
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from '../firebase';
import { WEDDING_CONFIG } from '../config/weddingConfig';
import { 
    sanitizeString, 
    isValidEmail, 
    isValidPhone, 
    isValidAccessCode,
    sanitizeSongRequest,
    isValidFoodChoice,
    isValidRsvpStatus,
    isValidRsvpType,
    isValidPlusOneName,
    isValidPlusOneContact
} from '../utils/validation';
import { 
    getGoogleCalendarUrl, 
    downloadICalFile, 
    getOutlookUrl 
} from '../utils/calendarUtils';
import { logError } from '../utils/logError';

const RSVP = () => {
    const [step, setStep] = useState(1); // 1: celebration code, 2: name selection, 3: form
    const [celebrationCode, setCelebrationCode] = useState('');
    const [guestList, setGuestList] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedGuest, setSelectedGuest] = useState(null);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [authError, setAuthError] = useState('');
    const suggestionsRef = useRef(null);
    const inputRef = useRef(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');
    
    const [guestData, setGuestData] = useState(null);
    const [formData, setFormData] = useState({
        email: '',
        phone: '',
        rsvpStatus: '',
        rsvpType: '',
        foodChoice: '',
        songRequest: '',
        message: '',
        hasPlusOne: false,
        plusOneFirstName: '',
        plusOneLastName: '',
        plusOneEmail: '',
        plusOnePhone: ''
    });

    // Check for access code in URL parameter and verify via Cloud Function
    useEffect(() => {
        const verifyUrlAccessCode = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            const accessCodeParam = urlParams.get('accessCode');
            
            if (accessCodeParam) {
                // Validate format first
                if (!isValidAccessCode(accessCodeParam)) {
                    setAuthError('Invalid code in URL. Please enter code manually.');
                    return;
                }
                
                setAuthError('Verifying wedding code...');
                
                try {
                    // Verify through Cloud Function
                    const verifyAccessCode = httpsCallable(functions, 'verifyAccessCode');
                    const result = await verifyAccessCode({ code: accessCodeParam });
                    
                    if (result.data.valid) {
                        // Store guest list and auto-progress to step 2
                        setGuestList(result.data.guests || []);
                        setStep(2);
                        setAuthError('');
                    }
                } catch (error) {
                    logError('URL_ACCESS_CODE_ERROR', error, { step: '1-url' });
                    setAuthError('Invalid wedding code in URL. Please enter code manually.');
                }
            }
        };
        
        verifyUrlAccessCode();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run once on mount

    // Close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (suggestionsRef.current && !suggestionsRef.current.contains(event.target) &&
                inputRef.current && !inputRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Check if before deadline
    const isBeforeDeadline = () => {
        const deadline = new Date(WEDDING_CONFIG.RSVP_DEADLINE);
        const today = new Date();
        return today <= deadline;
    };

    const getDeadlineText = () => {
        const deadline = new Date(WEDDING_CONFIG.RSVP_DEADLINE);
        const day = deadline.getDate();
        const month = deadline.toLocaleDateString('en-GB', { month: 'long' });
        const year = deadline.getFullYear();
        
        // Add ordinal suffix
        const getOrdinal = (n) => {
            const s = ['th', 'st', 'nd', 'rd'];
            const v = n % 100;
            return n + (s[(v - 20) % 10] || s[v] || s[0]);
        };
        
        return `${getOrdinal(day)} ${month} ${year}`;
    };

    // Handle celebration code submission
    const handleCelebrationCodeSubmit = async (e) => {
        e.preventDefault();
        
        // Validate code format
        if (!isValidAccessCode(celebrationCode)) {
            setAuthError('Invalid code format. Please check your invitation.');
            setCelebrationCode('');
            return;
        }
        
        setAuthError('Verifying...');
        
        try {
            // Call Cloud Function to verify access code securely
            const verifyAccessCode = httpsCallable(functions, 'verifyAccessCode');
            const result = await verifyAccessCode({ code: celebrationCode });
            
            if (result.data.valid) {
                setGuestList(result.data.guests || []);
                setStep(2);
                setAuthError('');
            }
        } catch (error) {
            logError('ACCESS_CODE_VERIFICATION_ERROR', error, { step: '1-manual' });
            
            // Handle specific error messages from Cloud Function
            if (error.code === 'functions/permission-denied') {
                setAuthError('Incorrect wedding code. Please check your invitation.');
            } else if (error.code === 'functions/resource-exhausted') {
                setAuthError('Too many attempts. Please try again in 15 minutes.');
            } else if (error.code === 'functions/invalid-argument') {
                setAuthError('Invalid code format. Please check your invitation.');
            } else {
                setAuthError('Unable to verify code. Please try again or contact us.');
            }
            
            setCelebrationCode('');
        }
    };

    // Handle name selection and load guest data
    const handleGuestSelect = async () => {
        if (!selectedGuest) return;
        setAuthError('');
        
        try {
            const guestRef = doc(db, "guests", selectedGuest.guestCode);
            const guestSnap = await getDoc(guestRef);
            
            if (guestSnap.exists()) {
                const guest = guestSnap.data();
                setGuestData(guest);
                
                // Pre-fill form with existing data
                setFormData({
                    email: guest.email || '',
                    phone: guest.phone || '',
                    rsvpStatus: guest.rsvpStatus || '',
                    rsvpType: guest.rsvpType || '',
                    foodChoice: guest.foodChoice || '',
                    songRequest: guest.songRequest || '',
                    message: guest.message || '',
                    hasPlusOne: guest.hasPlusOne || false,
                    plusOneFirstName: guest.plusOneFirstName || '',
                    plusOneLastName: guest.plusOneLastName || '',
                    plusOneEmail: guest.plusOneEmail || '',
                    plusOnePhone: guest.plusOnePhone || ''
                });
                
                setStep(3);
            } else {
                setAuthError('Could not load your details. Please try again.');
            }
        } catch (error) {
            logError('GUEST_LOAD_ERROR', error, { step: '2-name-select', guestCode: selectedGuest?.guestCode, guestName: selectedGuest?.displayName });
            setAuthError('Error loading guest data. Please try again.');
        }
    };

    // Filter guest list based on search query
    const filteredGuests = searchQuery.trim().length > 0
        ? guestList.filter(g => g.displayName.toLowerCase().includes(searchQuery.toLowerCase()))
        : [];

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!isBeforeDeadline()) {
            setSubmitMessage('The RSVP deadline has passed. Please contact us directly if you need to make changes.');
            return;
        }
        
        // Validate all inputs before submission
        if (!isValidEmail(formData.email)) {
            setSubmitMessage('Please enter a valid email address.');
            return;
        }
        
        if (!isValidPhone(formData.phone)) {
            setSubmitMessage('Please enter a valid phone number.');
            return;
        }
        
        if (!isValidRsvpStatus(formData.rsvpStatus)) {
            setSubmitMessage('Please select whether you are attending.');
            return;
        }
        
        if (formData.rsvpStatus === 'attending' && guestData.invitedAs !== 'evening' && !isValidRsvpType(formData.rsvpType, guestData.invitedAs)) {
            setSubmitMessage('Please select a valid attendance option.');
            return;
        }
        
        if (formData.foodChoice && !isValidFoodChoice(formData.foodChoice)) {
            setSubmitMessage('Please select a valid food choice.');
            return;
        }
        
        // Validate +1 fields if hasPlusOne is true
        if (formData.hasPlusOne) {
            if (!isValidPlusOneName(formData.plusOneFirstName)) {
                setSubmitMessage('Please enter a valid first name for your plus one.');
                return;
            }
            
            if (!isValidPlusOneName(formData.plusOneLastName)) {
                setSubmitMessage('Please enter a valid last name for your plus one.');
                return;
            }
            
            if (!isValidPlusOneContact(formData.plusOneEmail, formData.plusOnePhone)) {
                setSubmitMessage('Please enter valid contact information for your plus one (if provided).');
                return;
            }
        }
        
        setIsSubmitting(true);
        setSubmitMessage('');

        try {
            const guestRef = doc(db, "guests", selectedGuest.guestCode);
            
            // Sanitize all string inputs
            const updateData = {
                email: sanitizeString(formData.email),
                phone: sanitizeString(formData.phone),
                hasRSVPd: true,
                rsvpStatus: formData.rsvpStatus,
                rsvpType: guestData.invitedAs === 'evening' ? 'evening' : formData.rsvpType,
                songRequest: sanitizeSongRequest(formData.songRequest),
                message: sanitizeString(formData.message),
                hasPlusOne: formData.hasPlusOne,
                updatedAt: new Date()
            };
            
            // Add +1 details if they're bringing a plus one
            if (formData.hasPlusOne) {
                updateData.plusOneFirstName = sanitizeString(formData.plusOneFirstName);
                updateData.plusOneLastName = sanitizeString(formData.plusOneLastName);
                updateData.plusOneEmail = sanitizeString(formData.plusOneEmail);
                updateData.plusOnePhone = sanitizeString(formData.plusOnePhone);
            } else {
                // Clear +1 data if they're not bringing one
                updateData.plusOneFirstName = '';
                updateData.plusOneLastName = '';
                updateData.plusOneEmail = '';
                updateData.plusOnePhone = '';
            }
            
            // Only add food choice if guest is invited to all day and is attending all day
            if (guestData.invitedAs === 'allDay' && formData.rsvpType === 'allDay') {
                updateData.foodChoice = formData.foodChoice;
            }
            
            await updateDoc(guestRef, updateData);
            
            setSubmitMessage('RSVP submitted successfully! You can update your response anytime before ' + getDeadlineText() + '.');
            
            // Update local guest data
            setGuestData(prev => ({ ...prev, ...updateData }));
            
        } catch (error) {
            logError('RSVP_SUBMIT_ERROR', error, {
                step: '3-submit',
                guestCode: selectedGuest?.guestCode,
                guestName: selectedGuest?.displayName,
                rsvpStatus: formData.rsvpStatus
            });
            setSubmitMessage('Error submitting RSVP. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Reset to start
    const handleLogout = () => {
        setStep(1);
        setCelebrationCode('');
        setGuestList([]);
        setSearchQuery('');
        setSelectedGuest(null);
        setShowSuggestions(false);
        setGuestData(null);
        setFormData({
            email: '',
            phone: '',
            rsvpStatus: '',
            rsvpType: '',
            foodChoice: '',
            songRequest: '',
            message: '',
            hasPlusOne: false,
            plusOneFirstName: '',
            plusOneLastName: '',
            plusOneEmail: '',
            plusOnePhone: ''
        });
        setAuthError('');
        setSubmitMessage('');
    };

    // Return to name search without re-entering the wedding code
    const handleNewRsvp = () => {
        setStep(2);
        setSearchQuery('');
        setSelectedGuest(null);
        setShowSuggestions(false);
        setGuestData(null);
        setFormData({
            email: '',
            phone: '',
            rsvpStatus: '',
            rsvpType: '',
            foodChoice: '',
            songRequest: '',
            message: '',
            hasPlusOne: false,
            plusOneFirstName: '',
            plusOneLastName: '',
            plusOneEmail: '',
            plusOnePhone: ''
        });
        setAuthError('');
        setSubmitMessage('');
    };

    // Check if the +1 name matches an existing guest in the list
    const plusOneMatchedGuest = formData.hasPlusOne &&
        formData.plusOneFirstName.trim() &&
        formData.plusOneLastName.trim()
            ? guestList.find(g =>
                g.displayName.toLowerCase() ===
                `${formData.plusOneFirstName.trim()} ${formData.plusOneLastName.trim()}`.toLowerCase()
              ) || null
            : null;

    return (
        <div className="min-h-screen flex items-center justify-center" style={{backgroundColor: '#3d4f3d', paddingTop: '80px', paddingBottom: '40px'}}>
            <div className="max-w-4xl w-full mx-auto px-4">
                
                {/* RSVP Header */}
                <div className="text-center mb-12">
                    <h1 
                        className="text-6xl md:text-7xl mb-12" 
                        style={{
                            color: '#d4c5a0', 
                            fontFamily: 'Cinzel',
                            letterSpacing: '0.2em'
                        }}
                    >
                        RSVP
                    </h1>
                    
                    {/* Contact Info Row - Desktop */}
                    <div className="hidden md:flex justify-between items-start mb-12 max-w-5xl mx-auto">
                        {/* Becca */}
                        <div className="text-left flex-1">
                            <h2 className="text-4xl mb-4" style={{color: '#d4c5a0', fontFamily: 'Cinzel'}}>BECCA</h2>
                            <p className="text-sm" style={{color: '#d4c5a0', fontFamily: 'Tenor Sans'}}>
                                Telephone: 07916498314<br />
                                beccakpage@gmail.com
                            </p>
                        </div>
                        
                        {/* Center divider */}
                        <div className="flex-1 flex justify-center">
                            <div className="w-px h-24" style={{backgroundColor: '#d4c5a0'}}></div>
                        </div>
                        
                        {/* James */}
                        <div className="text-right flex-1">
                            <h2 className="text-4xl mb-4" style={{color: '#d4c5a0', fontFamily: 'Cinzel'}}>JAMES</h2>
                            <p className="text-sm" style={{color: '#d4c5a0', fontFamily: 'Tenor Sans'}}>
                                Telephone: 07429570995<br />
                                j.bmcgovern@hotmail.com
                            </p>
                        </div>
                    </div>
                    
                    {/* Contact Info - Mobile */}
                    <div className="md:hidden space-y-6 mb-12">
                        <div className="text-center">
                            <h2 className="text-3xl mb-2" style={{color: '#d4c5a0', fontFamily: 'Cinzel'}}>BECCA</h2>
                            <p className="text-sm" style={{color: '#d4c5a0', fontFamily: 'Tenor Sans'}}>
                                07916498314<br />
                                beccakpage@gmail.com
                            </p>
                        </div>
                        <div className="w-16 h-px mx-auto" style={{backgroundColor: '#d4c5a0'}}></div>
                        <div className="text-center">
                            <h2 className="text-3xl mb-2" style={{color: '#d4c5a0', fontFamily: 'Cinzel'}}>JAMES</h2>
                            <p className="text-sm" style={{color: '#d4c5a0', fontFamily: 'Tenor Sans'}}>
                                07429570995<br />
                                j.bmcgovern@hotmail.com
                            </p>
                        </div>
                    </div>
                </div>

                {/* Form Container */}
                <div className="max-w-md mx-auto">
                    
                    {/* Step 1: Celebration Code */}
                    {step === 1 && (
                        <div className="p-8 rounded-lg" style={{backgroundColor: 'rgba(212, 197, 160, 0.95)'}}>
                            <h2 className="text-3xl font-bold text-center mb-6" style={{color: '#3d4f3d', fontFamily: 'Cinzel', letterSpacing: '0.15em'}}>
                                WEDDING CODE
                            </h2>
                            <p className="text-center mb-6" style={{color: '#3d4f3d', fontFamily: 'Tenor Sans'}}>
                                Please enter the wedding code from your invitation to access the RSVP form.
                            </p>
                            
                            <form onSubmit={handleCelebrationCodeSubmit} className="space-y-4">
                                <div>
                                    <input
                                        type="text"
                                        id="celebrationCode"
                                        value={celebrationCode}
                                        onChange={(e) => setCelebrationCode(e.target.value)}
                                        required
                                        placeholder="Enter wedding code"
                                        className="mt-1 block w-full rounded-md border-2 shadow-sm focus:ring-2 p-3 text-center"
                                        style={{
                                            borderColor: '#3d4f3d',
                                            fontFamily: 'Tenor Sans',
                                            backgroundColor: 'white'
                                        }}
                                    />
                                </div>
                                
                                {authError && (
                                    <div className="text-red-700 text-sm text-center font-semibold" style={{fontFamily: 'Tenor Sans'}}>
                                        {authError}
                                    </div>
                                )}
                                
                                <button
                                    type="submit"
                                    className="w-full py-3 px-4 rounded-md shadow-sm font-medium text-white hover:opacity-90 transition-all duration-200"
                                    style={{backgroundColor: '#3d4f3d', fontFamily: 'Cinzel', letterSpacing: '0.1em'}}
                                >
                                    CONTINUE
                                </button>
                            </form>
                            
                            <p className="text-xs text-center mt-4" style={{color: '#3d4f3d', fontFamily: 'Tenor Sans'}}>
                                Can't find the wedding code? Please check your invitation or contact us.
                            </p>
                        </div>
                    )}

                    {/* Step 2: Name Selection */}
                    {step === 2 && (
                        <div className="p-8 rounded-lg" style={{backgroundColor: 'rgba(212, 197, 160, 0.95)'}}>
                            <h2 className="text-3xl font-bold text-center mb-6" style={{color: '#3d4f3d', fontFamily: 'Cinzel', letterSpacing: '0.15em'}}>
                                FIND YOUR NAME
                            </h2>
                            <p className="text-center mb-6" style={{color: '#3d4f3d', fontFamily: 'Tenor Sans'}}>
                                Start typing your name to find yourself in the guest list.
                            </p>
                            
                            <div className="space-y-4">
                                <div className="relative">
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => {
                                            setSearchQuery(e.target.value);
                                            setSelectedGuest(null);
                                            setShowSuggestions(true);
                                        }}
                                        onFocus={() => { if (searchQuery.trim().length > 0) setShowSuggestions(true); }}
                                        placeholder="Type your name..."
                                        className="mt-1 block w-full rounded-md border-2 shadow-sm focus:ring-2 p-3 text-center"
                                        style={{
                                            borderColor: '#3d4f3d',
                                            fontFamily: 'Tenor Sans',
                                            backgroundColor: 'white'
                                        }}
                                    />
                                    
                                    {showSuggestions && searchQuery.trim().length > 0 && (
                                        <div
                                            ref={suggestionsRef}
                                            className="absolute z-10 w-full mt-1 rounded-md border-2 shadow-lg overflow-y-auto"
                                            style={{
                                                borderColor: '#3d4f3d',
                                                backgroundColor: 'white',
                                                maxHeight: '200px'
                                            }}
                                        >
                                            {filteredGuests.length > 0 ? (
                                                filteredGuests.map((guest) => (
                                                    <button
                                                        key={guest.guestCode}
                                                        type="button"
                                                        onClick={() => {
                                                            setSelectedGuest(guest);
                                                            setSearchQuery(guest.displayName);
                                                            setShowSuggestions(false);
                                                        }}
                                                        className="w-full text-left px-4 py-2 hover:bg-opacity-20 transition-colors duration-150"
                                                        style={{
                                                            fontFamily: 'Tenor Sans',
                                                            color: '#3d4f3d',
                                                            backgroundColor: 'transparent'
                                                        }}
                                                        onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(61, 79, 61, 0.1)'}
                                                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                                    >
                                                        {guest.displayName}
                                                    </button>
                                                ))
                                            ) : (
                                                <p className="px-4 py-2 text-sm" style={{color: '#3d4f3d', fontFamily: 'Tenor Sans'}}>
                                                    No matching guests found.
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                                
                                {authError && (
                                    <div className="text-red-700 text-sm text-center font-semibold" style={{fontFamily: 'Tenor Sans'}}>
                                        {authError}
                                    </div>
                                )}
                                
                                <button
                                    type="button"
                                    onClick={handleGuestSelect}
                                    disabled={!selectedGuest}
                                    className="w-full py-3 px-4 rounded-md shadow-sm font-medium text-white transition-all duration-200"
                                    style={{
                                        backgroundColor: selectedGuest ? '#3d4f3d' : '#9ca3af',
                                        fontFamily: 'Cinzel',
                                        letterSpacing: '0.1em',
                                        cursor: selectedGuest ? 'pointer' : 'not-allowed',
                                        opacity: selectedGuest ? 1 : 0.7
                                    }}
                                >
                                    ACCESS RSVP FORM
                                </button>
                                
                                <button
                                    type="button"
                                    onClick={() => setStep(1)}
                                    className="w-full text-sm hover:underline"
                                    style={{color: '#3d4f3d', fontFamily: 'Tenor Sans'}}
                                >
                                    Back
                                </button>
                            </div>
                            
                            <p className="text-xs text-center mt-4" style={{color: '#3d4f3d', fontFamily: 'Tenor Sans'}}>
                                Can't find your name? Please contact us for assistance.
                            </p>
                        </div>
                    )}

                    {/* Step 3: RSVP Form */}
                    {step === 3 && guestData && (
                        <div className="p-8 rounded-lg" style={{backgroundColor: 'rgba(212, 197, 160, 0.95)'}}>
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-2xl font-bold" style={{color: '#3d4f3d', fontFamily: 'Cinzel'}}>
                                    {guestData.firstName} {guestData.lastName}
                                </h2>
                                <button
                                    onClick={handleLogout}
                                    className="text-sm hover:underline"
                                    style={{color: '#3d4f3d', fontFamily: 'Tenor Sans'}}
                                >
                                    Logout
                                </button>
                            </div>
                            
                            <div className="mb-4 p-3 rounded-md" style={{backgroundColor: 'rgba(61, 79, 61, 0.1)', border: '1px solid #3d4f3d'}}>
                                <p className="text-sm" style={{color: '#3d4f3d', fontFamily: 'Tenor Sans'}}>
                                    You are invited as an <strong>{guestData.invitedAs === 'allDay' ? 'All Day' : 'Evening'}</strong> guest.
                                </p>
                                <p className="text-xs mt-1" style={{color: '#3d4f3d', fontFamily: 'Tenor Sans'}}>
                                    You can update your RSVP until {getDeadlineText()}.
                                </p>
                            </div>

                            {!isBeforeDeadline() && (
                                <div className="mb-4 p-3 rounded-md" style={{backgroundColor: 'rgba(220, 53, 69, 0.1)', border: '1px solid #dc3545'}}>
                                    <p className="text-sm" style={{color: '#721c24', fontFamily: 'Tenor Sans'}}>
                                        The RSVP deadline has passed. Please contact us directly if you need to make changes.
                                    </p>
                                </div>
                            )}
                            
                            <form onSubmit={handleSubmit} className="space-y-4">
                                {/* RSVP Status */}
                                <div>
                                    <label className="block text-sm font-medium mb-2" style={{color: '#3d4f3d', fontFamily: 'Tenor Sans'}}>
                                        Will you be attending? *
                                    </label>
                                    <select
                                        name="rsvpStatus"
                                        value={formData.rsvpStatus}
                                        onChange={handleInputChange}
                                        required
                                        className="mt-1 block w-full rounded-md border-2 shadow-sm focus:ring-2 p-2"
                                        style={{borderColor: '#3d4f3d', fontFamily: 'Tenor Sans', backgroundColor: 'white'}}
                                    >
                                        <option value="">Please select...</option>
                                        <option value="attending">Yes, I will attend</option>
                                        <option value="not-attending">Sorry, I cannot attend</option>
                                    </select>
                                </div>

                                {/* If attending and invited all day, show RSVP Type */}
                                {formData.rsvpStatus === 'attending' && guestData.invitedAs === 'allDay' && (
                                    <div>
                                        <label className="block text-sm font-medium mb-2" style={{color: '#3d4f3d', fontFamily: 'Tenor Sans'}}>
                                            Which part will you attend? *
                                        </label>
                                        <select
                                            name="rsvpType"
                                            value={formData.rsvpType}
                                            onChange={handleInputChange}
                                            required
                                            className="mt-1 block w-full rounded-md border-2 shadow-sm focus:ring-2 p-2"
                                            style={{borderColor: '#3d4f3d', fontFamily: 'Tenor Sans', backgroundColor: 'white'}}
                                        >
                                            <option value="">Please select...</option>
                                            <option value="allDay">All Day (Ceremony & Reception)</option>
                                            <option value="evening">Evening Reception Only</option>
                                        </select>
                                    </div>
                                )}

                                {/* Food Choice - Only for all day guests attending all day */}
                                {formData.rsvpStatus === 'attending' && formData.rsvpType === 'allDay' && guestData.invitedAs === 'allDay' ? (
                                    <div>
                                        <label className="block text-sm font-medium mb-2" style={{color: '#3d4f3d', fontFamily: 'Tenor Sans'}}>
                                            Food Choice *
                                        </label>
                                        <select
                                            name="foodChoice"
                                            value={formData.foodChoice}
                                            onChange={handleInputChange}
                                            required
                                            className="mt-1 block w-full rounded-md border-2 shadow-sm focus:ring-2 p-2"
                                            style={{borderColor: '#3d4f3d', fontFamily: 'Tenor Sans', backgroundColor: 'white'}}
                                        >
                                            <option value="">Please select...</option>
                                            <option value="Meatball Lasagna">Meatball Lasagna</option>
                                            <option value="Carbonara">Carbonara</option>
                                            <option value="Vegetarian - Please ask for Details">Vegetarian - Please ask for Details</option>
                                        </select>
                                        <p className="text-xs mt-1" style={{color: '#3d4f3d', fontFamily: 'Tenor Sans'}}>Includes a Potato & Salad Bar</p>
                                        <p className="text-xs" style={{color: '#3d4f3d', fontFamily: 'Tenor Sans'}}>Dessert: Cheese Board or Tiramisu</p>
                                        <p className="text-xs" style={{color: '#3d4f3d', fontFamily: 'Tenor Sans'}}>Evenly divided between guests, first come first served!</p>
                                    </div>
                                ) : null}

                                {/* Song Request, Email, and Phone - Only show if attending */}
                                {formData.rsvpStatus === 'attending' && (
                                    <>
                                        {/* Song Request - Optional */}
                                        <div>
                                            <label htmlFor="songRequest" className="block text-sm font-medium" style={{color: '#3d4f3d', fontFamily: 'Tenor Sans'}}>
                                                Song Request (Optional)
                                            </label>
                                            <input
                                                type="text"
                                                id="songRequest"
                                                name="songRequest"
                                                value={formData.songRequest}
                                                onChange={handleInputChange}
                                                placeholder="Request a song you'd like to hear"
                                                className="mt-1 block w-full rounded-md border-2 shadow-sm focus:ring-2 p-2"
                                                style={{borderColor: '#3d4f3d', fontFamily: 'Tenor Sans', backgroundColor: 'white'}}
                                            />
                                            <p className="text-xs mt-1" style={{color: '#3d4f3d', fontFamily: 'Tenor Sans'}}>
                                                We'll do our best to play your requested song!
                                            </p>
                                        </div>


                                        {/* Contact Information */}
                                        <div>
                                            <label htmlFor="email" className="block text-sm font-medium" style={{color: '#3d4f3d', fontFamily: 'Tenor Sans'}}>
                                                Email Address *
                                            </label>
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                required
                                                className="mt-1 block w-full rounded-md border-2 shadow-sm focus:ring-2 p-2"
                                                style={{borderColor: '#3d4f3d', fontFamily: 'Tenor Sans', backgroundColor: 'white'}}
                                            />
                                        </div>

                                        <div>
                                            <label htmlFor="phone" className="block text-sm font-medium" style={{color: '#3d4f3d', fontFamily: 'Tenor Sans'}}>
                                                Phone Number *
                                            </label>
                                            <input
                                                type="tel"
                                                id="phone"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                required
                                                className="mt-1 block w-full rounded-md border-2 shadow-sm focus:ring-2 p-2"
                                                style={{borderColor: '#3d4f3d', fontFamily: 'Tenor Sans', backgroundColor: 'white'}}
                                            />
                                        </div>

                                        {/* Plus One Section */}
                                        <div className="space-y-4 p-4 rounded-md border-2" style={{borderColor: 'rgba(61, 79, 61, 0.3)'}}>
                                            {/* Info text for all-day guests */}
                                            {guestData.invitedAs === 'allDay' && (
                                                <div className="p-3 rounded-md" style={{backgroundColor: 'rgba(61, 79, 61, 0.05)', border: '1px solid rgba(61, 79, 61, 0.2)'}}>
                                                    <p className="text-xs" style={{color: '#3d4f3d', fontFamily: 'Tenor Sans'}}>
                                                        Plus ones are welcome from 18:00 onwards for the evening reception
                                                    </p>
                                                </div>
                                            )}

                                            {/* Static +1 note */}
                                            <p className="text-xs" style={{color: '#3d4f3d', fontFamily: 'Tenor Sans'}}>
                                                Your +1 may already be on the guest list with their own RSVP, please check and complete their RSVP separately before adding as a plus one.
                                            </p>
                                            
                                            {/* Plus One Checkbox */}
                                            <div className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    id="hasPlusOne"
                                                    name="hasPlusOne"
                                                    checked={formData.hasPlusOne}
                                                    onChange={handleInputChange}
                                                    className="h-4 w-4 rounded border-2 focus:ring-2"
                                                    style={{borderColor: '#3d4f3d', color: '#3d4f3d'}}
                                                />
                                                <label htmlFor="hasPlusOne" className="ml-2 text-sm font-medium" style={{color: '#3d4f3d', fontFamily: 'Tenor Sans'}}>
                                                    Will you be bringing a +1?
                                                </label>
                                            </div>

                                            {/* Plus One Details - Show when checkbox is checked */}
                                            {formData.hasPlusOne && (
                                                <div className="pl-6 space-y-4 border-l-2" style={{borderColor: 'rgba(61, 79, 61, 0.2)'}}>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        <div>
                                                            <label htmlFor="plusOneFirstName" className="block text-sm font-medium" style={{color: '#3d4f3d', fontFamily: 'Tenor Sans'}}>
                                                                First Name *
                                                            </label>
                                                            <input
                                                                type="text"
                                                                id="plusOneFirstName"
                                                                name="plusOneFirstName"
                                                                value={formData.plusOneFirstName}
                                                                onChange={handleInputChange}
                                                                required
                                                                className="mt-1 block w-full rounded-md border-2 shadow-sm focus:ring-2 p-2"
                                                                style={{borderColor: '#3d4f3d', fontFamily: 'Tenor Sans', backgroundColor: 'white'}}
                                                            />
                                                        </div>
                                                        
                                                        <div>
                                                            <label htmlFor="plusOneLastName" className="block text-sm font-medium" style={{color: '#3d4f3d', fontFamily: 'Tenor Sans'}}>
                                                                Last Name *
                                                            </label>
                                                            <input
                                                                type="text"
                                                                id="plusOneLastName"
                                                                name="plusOneLastName"
                                                                value={formData.plusOneLastName}
                                                                onChange={handleInputChange}
                                                                required
                                                                className="mt-1 block w-full rounded-md border-2 shadow-sm focus:ring-2 p-2"
                                                                style={{borderColor: '#3d4f3d', fontFamily: 'Tenor Sans', backgroundColor: 'white'}}
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Inline warning if +1 name matches an existing guest */}
                                                    {plusOneMatchedGuest && (
                                                        <div className="p-3 rounded-md" style={{backgroundColor: 'rgba(180, 120, 0, 0.1)', border: '1px solid rgba(180, 120, 0, 0.5)'}}>
                                                            <p className="text-sm font-semibold" style={{color: '#7a4f00', fontFamily: 'Tenor Sans'}}>
                                                                {formData.plusOneFirstName.trim()} {formData.plusOneLastName.trim()} already has their own invitation.
                                                            </p>
                                                            <p className="text-xs mt-1" style={{color: '#7a4f00', fontFamily: 'Tenor Sans'}}>
                                                                Please complete their separate RSVP using the wedding code or click 'Submit A Different RSVP' at the bottom of the page.
                                                            </p>
                                                            <p className="text-xs mt-1" style={{color: '#7a4f00', fontFamily: 'Tenor Sans'}}>
                                                                If this is likely to be a different person, please check with us or continue with your +1!
                                                            </p>
                                                        </div>
                                                    )}

                                                    <div>
                                                        <label htmlFor="plusOneEmail" className="block text-sm font-medium" style={{color: '#3d4f3d', fontFamily: 'Tenor Sans'}}>
                                                            Email Address (Optional)
                                                        </label>
                                                        <input
                                                            type="email"
                                                            id="plusOneEmail"
                                                            name="plusOneEmail"
                                                            value={formData.plusOneEmail}
                                                            onChange={handleInputChange}
                                                            className="mt-1 block w-full rounded-md border-2 shadow-sm focus:ring-2 p-2"
                                                            style={{borderColor: '#3d4f3d', fontFamily: 'Tenor Sans', backgroundColor: 'white'}}
                                                        />
                                                    </div>
                                                    
                                                    <div>
                                                        <label htmlFor="plusOnePhone" className="block text-sm font-medium" style={{color: '#3d4f3d', fontFamily: 'Tenor Sans'}}>
                                                            Phone Number (Optional)
                                                        </label>
                                                        <input
                                                            type="tel"
                                                            id="plusOnePhone"
                                                            name="plusOnePhone"
                                                            value={formData.plusOnePhone}
                                                            onChange={handleInputChange}
                                                            className="mt-1 block w-full rounded-md border-2 shadow-sm focus:ring-2 p-2"
                                                            style={{borderColor: '#3d4f3d', fontFamily: 'Tenor Sans', backgroundColor: 'white'}}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Message for the couple - Optional */}
                                        <div>
                                            <label htmlFor="message" className="block text-sm font-medium" style={{color: '#3d4f3d', fontFamily: 'Tenor Sans'}}>
                                                Any further questions or messages for the couple? (Optional)
                                            </label>
                                            <textarea
                                                id="message"
                                                name="message"
                                                value={formData.message}
                                                onChange={handleInputChange}
                                                placeholder="Leave a message or ask us anything..."
                                                rows={3}
                                                className="mt-1 block w-full rounded-md border-2 shadow-sm focus:ring-2 p-2"
                                                style={{borderColor: '#3d4f3d', fontFamily: 'Tenor Sans', backgroundColor: 'white', resize: 'vertical'}}
                                            />
                                        </div>
                                    </>
                                )}

                                {submitMessage && (
                                    <div className={`p-3 rounded-md ${submitMessage.includes('Error') ? 'bg-red-100 border border-red-300' : 'bg-green-100 border border-green-300'}`}>
                                        <p className={`text-sm ${submitMessage.includes('Error') ? 'text-red-800' : 'text-green-800'}`} style={{fontFamily: 'Tenor Sans'}}>
                                            {submitMessage}
                                        </p>
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={isSubmitting || !isBeforeDeadline()}
                                    className="w-full py-3 px-4 rounded-md shadow-sm font-medium text-white hover:opacity-90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                    style={{backgroundColor: '#3d4f3d', fontFamily: 'Cinzel', letterSpacing: '0.1em'}}
                                >
                                    {isSubmitting ? 'SUBMITTING...' : guestData.hasRSVPd ? 'UPDATE RSVP' : 'SUBMIT RSVP'}
                                </button>
                            </form>

                            {/* Submit A Different RSVP - only shown when +1 matches an existing guest */}
                            {plusOneMatchedGuest && (
                                <div className="mt-4">
                                    <button
                                        type="button"
                                        onClick={() => window.open('https://bexandjamestietheknot.com/rsvp?accessCode=ForeverAndAlways', '_blank')}
                                        className="w-full py-3 px-4 rounded-md shadow-sm font-medium hover:opacity-90 transition-all duration-200"
                                        style={{backgroundColor: 'rgba(180, 120, 0, 0.15)', color: '#7a4f00', fontFamily: 'Cinzel', letterSpacing: '0.1em', border: '1px solid rgba(180, 120, 0, 0.5)'}}
                                    >
                                        SUBMIT A DIFFERENT RSVP
                                    </button>
                                </div>
                            )}

                            {/* Submit a new RSVP for another guest without re-entering the wedding code */}
                            <div className="mt-4">
                                <button
                                    type="button"
                                    onClick={handleNewRsvp}
                                    className="w-full py-3 px-4 rounded-md shadow-sm font-medium hover:opacity-90 transition-all duration-200"
                                    style={{backgroundColor: 'rgba(61, 79, 61, 0.08)', color: '#3d4f3d', fontFamily: 'Cinzel', letterSpacing: '0.1em', border: '1px solid rgba(61, 79, 61, 0.3)'}}
                                >
                                    SUBMIT A NEW RSVP
                                </button>
                            </div>

                            {/* Add to Calendar Section */}
                            {formData.rsvpStatus !== 'not-attending' && (
                            <div className="mt-6 p-4 rounded-md" style={{backgroundColor: 'rgba(61, 79, 61, 0.05)', border: '1px solid rgba(61, 79, 61, 0.2)'}}>
                                <h3 className="text-sm font-semibold mb-3 text-center" style={{color: '#3d4f3d', fontFamily: 'Cinzel', letterSpacing: '0.1em'}}>
                                    ADD TO CALENDAR
                                </h3>
                                <p className="text-xs text-center mb-3" style={{color: '#3d4f3d', fontFamily: 'Tenor Sans'}}>
                                    24th October 2026 • Field Place, Worthing
                                </p>
                                <div className="flex flex-wrap gap-2 justify-center">
                                    <button
                                        onClick={() => window.open(getGoogleCalendarUrl(), '_blank')}
                                        className="px-3 py-2 text-xs rounded-md transition-all duration-200 hover:opacity-80 flex items-center gap-1"
                                        style={{backgroundColor: '#3d4f3d', color: '#d4c5a0', fontFamily: 'Tenor Sans'}}
                                    >
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M19,4H18V2H16V4H8V2H6V4H5C3.89,4 3,4.9 3,6V20A2,2 0 0,0 5,22H19A2,2 0 0,0 21,20V6A2,2 0 0,0 19,4M19,20H5V10H19V20Z"/>
                                        </svg>
                                        Google
                                    </button>
                                    <button
                                        onClick={downloadICalFile}
                                        className="px-3 py-2 text-xs rounded-md transition-all duration-200 hover:opacity-80 flex items-center gap-1"
                                        style={{backgroundColor: '#3d4f3d', color: '#d4c5a0', fontFamily: 'Tenor Sans'}}
                                    >
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M19,4H18V2H16V4H8V2H6V4H5C3.89,4 3,4.9 3,6V20A2,2 0 0,0 5,22H19A2,2 0 0,0 21,20V6A2,2 0 0,0 19,4M19,20H5V10H19V20Z"/>
                                        </svg>
                                        Apple
                                    </button>
                                    <button
                                        onClick={() => window.open(getOutlookUrl(), '_blank')}
                                        className="px-3 py-2 text-xs rounded-md transition-all duration-200 hover:opacity-80 flex items-center gap-1"
                                        style={{backgroundColor: '#3d4f3d', color: '#d4c5a0', fontFamily: 'Tenor Sans'}}
                                    >
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M19,4H18V2H16V4H8V2H6V4H5C3.89,4 3,4.9 3,6V20A2,2 0 0,0 5,22H19A2,2 0 0,0 21,20V6A2,2 0 0,0 19,4M19,20H5V10H19V20Z"/>
                                        </svg>
                                        Outlook
                                    </button>
                                    <button
                                        onClick={downloadICalFile}
                                        className="px-3 py-2 text-xs rounded-md transition-all duration-200 hover:opacity-80 flex items-center gap-1"
                                        style={{backgroundColor: '#3d4f3d', color: '#d4c5a0', fontFamily: 'Tenor Sans'}}
                                    >
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M7,9V11H17V9H7M7,13V15H17V13H7Z"/>
                                        </svg>
                                        Other
                                    </button>
                                </div>
                            </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RSVP;
